import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import YTPlayer from 'youtube-player'
import { YouTubePlayer } from 'youtube-player/dist/types'
import { trackNavigationVideoPlay, trackNavigationModuleClick, trackCategoriesPageClick, trackSolutionVideoPage } from 'common/track'
import { youtubeId } from 'common/util'
import styles from './index.module.scss'

const RATE = 360 / 640

enum STATUS {
  unstarted = -1,
  ended = 0,
  playing = 1,
  paused = 2,
  buffering = 3,
  cued = 5,
}

interface IVideoProps {
  pn?: string
  language?: string
  sub_module?: string
  description: string | null
  layout?: string
  id: number
  title: string
  value: string
  art_id: number
  module: string
  isPlaying?: boolean
  style?: React.CSSProperties
  uid?: string
}

const Video = React.memo(
  ({
    pn,
    language,
    sub_module,
    value: url,
    layout,
    id,
    title,
    description,
    art_id,
    module,
    style,
    isPlaying,
    uid,
  }: IVideoProps) => {
    const videoId = useMemo(() => youtubeId(url || ''), [url])
    // 使用useState生成一个唯一的标识符，确保url相同 video组件渲染异常
    const [uniqueId] = useState(() => {
      return `video-${Math.random().toString(36).substr(2, 9)}`
    })
    const container = useRef<HTMLDivElement>(null)
    const player = useRef<YouTubePlayer | null>(null)
    const playStart = useRef(Date.now())
    const videoRef = useRef(null) // 假设这是Video组件内部的ref
    const videoOverlayRef = useRef(null) as any

    const trackVideoPlay = useCallback(
      (duration: number) => {
        // 文章集合页点击视频埋点
        if (module === 'tutorial-catagory_page') {
          trackCategoriesPageClick(title, uid, language, sub_module)
        } else {
          trackNavigationVideoPlay(Math.ceil(duration / 1000), videoId, art_id, pn, language, sub_module)
        }
      },
      [module, title, uid, language, sub_module, videoId, art_id, pn]
    )

    // swiper 滑动时视频暂停播放
    useEffect(() => {
      if (isPlaying && player.current && layout === 'horizontal') {
        player.current.pauseVideo()
      }
    }, [isPlaying, layout])


    useEffect(() => {
      if (!container.current) return
      if (player.current) return
      if (!videoId) return
      const { offsetWidth: width } = container.current
      const height = RATE * width
      player.current = YTPlayer(uniqueId, { videoId, width, height })

      // 播放器动作
      player.current.on('stateChange', (e: { data: STATUS }) => {
        
        if (e.data === STATUS.playing) {
          playStart.current = Date.now()
          if (sub_module === 'solution') {
            trackSolutionVideoPage(uid as string, url)
          } else {
            trackNavigationModuleClick(title || '', { id: art_id, module, value: videoId, pn, language, sub_module })
          }
        } else if ([STATUS.ended, STATUS.paused, STATUS.cued].includes(e.data)) {
          trackVideoPlay(Date.now() - playStart.current)
          playStart.current = Date.now()
        }
        // 监听如果暂停时，将pointer-events设置为auto
        if (e.data === STATUS.paused && layout === 'horizontal') {
          const overlay = videoOverlayRef.current
          overlay.style.pointerEvents = 'auto'
        }
      })

      // JavaScript事件转发
      // 由于swiper无法滑动问题，因为iframe链接的是外部页面，导致swiper不能监听滑动事件
      // 所以由样式 pointer-events: none 控制
      if (layout === 'horizontal') {
        videoOverlayRef.current.addEventListener('click', function () {
          const overlay = videoOverlayRef.current
          if (overlay) {
            overlay.style.pointerEvents = 'none'
            if (player.current) {
              player.current.playVideo()
            }
          }
        })
      }
    }, [videoId, trackVideoPlay, art_id, module, title, layout, uniqueId, pn, sub_module, language, uid, url])

   
    return (
        <div className={styles.videoContent} style={style}>
          <div className={styles.video} ref={container}>
            {videoId ? (
              <>
                <div id={uniqueId} ref={videoRef}></div>
                {layout === 'horizontal' && <div className={styles.videoOverlay} ref={videoOverlayRef}></div>}
              </>
            ) : (
              <div className={styles.error}>
                <svg height="48" version="1.1" viewBox="0 0 68 48" width="68">
                  <path
                    className="ytp-large-play-button-bg"
                    d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                    fill="#f00"
                  ></path>
                  <path d="M 45,24 27,14 27,34" fill="#fff"></path>
                </svg>
                <span>Video Cannot Be Played</span>
              </div>
            )}
          </div>
          <div className={styles.title}>{title && <p>{title}</p>}</div>
        </div>
    )
  },
  (prevProps, nextProps) => {
    return prevProps.isPlaying === nextProps.isPlaying && prevProps.value === nextProps.value
  }
)

export default Video
